/**
 * Tela do RH doméstico: quadro de colaboradores e banco de horas.
 *
 * Papel: junta as duas abas do módulo — Funcionários, com as duas tabelas do
 * quadro, a caixa de funcionário, a confirmação de exclusão e a seleção da
 * equipe ativa com as exportações, e Banco de Horas Equipe FBV, que é a
 * `AbaBancoHoras`.
 *
 * Como usar: só a aba escolhida em `aba` desenha a sua parte — as duas nunca
 * aparecem juntas. A tela do banco de horas, o resumo ou um colaborador, fica
 * guardada aqui em `telaDoBanco`, e não dentro da aba: passar por Funcionários
 * e voltar encontra a mesma pessoa aberta, como na origem. Quem tem nível `ver`
 * também não vê, na aba do banco de horas, nenhum botão que grave.
 *
 * A aba Funcionários tem duas tabelas, uma para quem está `ativo` ou
 * `diarista` e outra para quem está `desligado`, porque é assim que o quadro se
 * lê: quem passou pela casa continua no cadastro, mas não na mesma lista de
 * quem trabalha hoje. Quem tem nível `ver` não recebe `onNovo`, `onEditar` nem
 * `onRemover`, e por isso não vê botão de ação nenhum.
 *
 * Excluir um colaborador que o calendário, a escala ou uma ausência ainda cita responde 409, e
 * a mensagem aparece dentro da caixa de confirmação.
 *
 * As linhas da "Equipe ativa" se marcam uma a uma ou todas pelo cabeçalho, em
 * qualquer nível de acesso — exportar é leitura —, e com alguém marcado a
 * `BarraDeSelecao` aparece entre o cabeçalho da seção e a tabela. A marcação
 * guarda o `id` da pessoa em `selecionados` e só conta quem continua na
 * equipe ativa: a pessoa marcada que é desligada, ou excluída, sai da contagem
 * e da exportação sem precisar ser desmarcada. A folha e a planilha recebem as
 * pessoas por `ordenarPorNome`.
 *
 * Toda folha de impressão é montada aqui, em `folha`, fora do bloco da tela: a
 * da equipe marcada ("🖨 Exportar PDF") e as duas do banco de horas, que a
 * `AbaBancoHoras` pede com a lista ou o colaborador do momento do clique.
 * Enquanto uma folha está montada, todo o resto da página leva `print:hidden`
 * — o papel sai só com ela. A folha recebe o instante do clique como `key`,
 * novo a cada clique, e é isso que faz um segundo clique imprimir de novo
 * mesmo quando o navegador não avisou que a primeira janela de impressão
 * fechou. "⇩ Exportar Excel" chama `exportarPlanilha`, e a falha em carregar a
 * biblioteca aparece embaixo da barra, sem dado nenhum da equipe no texto.
 */
import {
  type Colaborador,
  type ColaboradorBancoHoras,
  acessoAlcanca,
} from '@concierge/shared';
import { useState } from 'react';

import { ErroDaApi } from '@/shared/api';
import { useAcesso } from '@/shared/hooks';
import { exportarPlanilha } from '@/shared/lib/planilha';
import { cn } from '@/shared/lib/utils';
import { hojeEmIso } from '@/shared/lib/vencimento';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import { Button } from '@/shared/ui/button';
import { CabecalhoDeSecao } from '@/shared/ui/cabecalho-de-secao';
import { CabecalhoDoModulo } from '@/shared/ui/cabecalho-do-modulo';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/ui/dialog';
import { SecaoCadastro } from '@/shared/ui/secao-cadastro';
import { AbaBancoHoras, type TelaDoBanco } from '../components/aba-banco-horas';
import { BarraDeSelecao } from '../components/barra-de-selecao';
import { FolhaColaboradorBancoHoras } from '../components/folha-colaborador-banco-horas';
import { FolhaDaEquipe } from '../components/folha-da-equipe';
import { FolhaResumoBancoHoras } from '../components/folha-resumo-banco-horas';
import { FormularioColaborador } from '../components/formulario-colaborador';
import { TabelaColaboradores } from '../components/tabela-colaboradores';
import { ordenarPorNome, planilhaDaEquipe } from '../equipe-selecionada';
import { useColaboradores, useExcluirColaborador } from '../hooks/usar-rh';

/** Qual das duas abas está na tela. */
type Aba = 'funcionarios' | 'bancohoras';

/** Em que estado a caixa de funcionário está: fechada, criando ou editando alguém. */
type PainelAberto =
  | { modo: 'fechado' }
  | { modo: 'novo-colaborador' }
  | { modo: 'edicao-colaborador'; colaborador: Colaborador };

/** A folha de impressão montada, com o instante do clique que a pediu. */
type FolhaAberta =
  | { tipo: 'equipe'; exportadaEm: string }
  | {
      tipo: 'banco-resumo';
      colaboradores: readonly ColaboradorBancoHoras[];
      exportadaEm: string;
    }
  | { tipo: 'banco-colaborador'; colaborador: ColaboradorBancoHoras; exportadaEm: string };

/**
 * Página do RH doméstico.
 *
 * @returns A tela com as duas abas, a caixa de funcionário e a confirmação.
 */
export function PaginaRh() {
  const [aba, setAba] = useState<Aba>('funcionarios');
  const [telaDoBanco, setTelaDoBanco] = useState<TelaDoBanco>({ modo: 'resumo' });
  const [painel, setPainel] = useState<PainelAberto>({ modo: 'fechado' });
  const [aRemover, setARemover] = useState<Colaborador | null>(null);
  const [erroDaRemocao, setErroDaRemocao] = useState<string | null>(null);
  const [selecionados, setSelecionados] = useState<ReadonlySet<string>>(new Set());
  const [folha, setFolha] = useState<FolhaAberta | null>(null);
  const [gerandoPlanilha, setGerandoPlanilha] = useState(false);
  const [planilhaFalhou, setPlanilhaFalhou] = useState(false);

  const acesso = useAcesso();
  const podeEditar = acesso.data !== undefined && acessoAlcanca(acesso.data, 'rh', 'editar');

  const consultaPessoas = useColaboradores();
  const excluirColaborador = useExcluirColaborador();

  const todosOsColaboradores = consultaPessoas.data ?? [];
  const colaboradoresAtivos = todosOsColaboradores.filter(
    (colaborador) => colaborador.status !== 'desligado',
  );
  const colaboradoresDesligados = todosOsColaboradores.filter(
    (colaborador) => colaborador.status === 'desligado',
  );
  const equipeSelecionada = ordenarPorNome(
    colaboradoresAtivos.filter((colaborador) => selecionados.has(colaborador.id)),
  );

  /** Fecha a caixa, seja pelo botão, pelo `Esc` ou por clique no véu em volta dela. */
  function handlePainelOpenChange(aberto: boolean) {
    if (!aberto) setPainel({ modo: 'fechado' });
  }

  /** Marca ou desmarca a pessoa da linha. */
  function handleAlternarSelecao(colaborador: Colaborador, marcado: boolean) {
    setSelecionados((atual) => {
      const proximo = new Set(atual);
      if (marcado) proximo.add(colaborador.id);
      else proximo.delete(colaborador.id);
      return proximo;
    });
  }

  /** Marca ou desmarca de uma vez todas as pessoas da equipe ativa. */
  function handleAlternarTodos(marcado: boolean) {
    setSelecionados((atual) => {
      const proximo = new Set(atual);
      for (const colaborador of colaboradoresAtivos) {
        if (marcado) proximo.add(colaborador.id);
        else proximo.delete(colaborador.id);
      }
      return proximo;
    });
  }

  /** Desmarca todo mundo, o que também faz a barra sumir. */
  function handleLimparSelecao() {
    setSelecionados(new Set());
    setPlanilhaFalhou(false);
  }

  /** Monta a folha de impressão, com o instante do clique no subtítulo. */
  function handleExportarPdf() {
    setFolha({ tipo: 'equipe', exportadaEm: new Date().toISOString() });
  }

  /** Gera a planilha da equipe marcada, com a data de hoje no nome do arquivo. */
  async function handleExportarExcel() {
    setGerandoPlanilha(true);
    setPlanilhaFalhou(false);
    try {
      await exportarPlanilha(planilhaDaEquipe(equipeSelecionada, hojeEmIso()));
    } catch {
      setPlanilhaFalhou(true);
    } finally {
      setGerandoPlanilha(false);
    }
  }

  /** Abre a confirmação de exclusão, limpando a falha da tentativa anterior. */
  function handlePedirRemocao(colaborador: Colaborador) {
    setErroDaRemocao(null);
    setARemover(colaborador);
  }

  /** Fecha a confirmação de exclusão, com a falha anterior junto. */
  function handleRemocaoOpenChange(aberto: boolean) {
    if (aberto) return;
    setARemover(null);
    setErroDaRemocao(null);
  }

  /** Apaga o colaborador confirmado e fecha a caixa, se a API aceitar. */
  async function handleConfirmarRemocao() {
    if (aRemover === null) return;
    setErroDaRemocao(null);

    try {
      await excluirColaborador.mutateAsync(aRemover.id);
      setARemover(null);
    } catch (erro) {
      setErroDaRemocao(
        erro instanceof ErroDaApi
          ? erro.message
          : 'A exclusão falhou antes de chegar ao servidor.',
      );
    }
  }

  return (
    <>
      <div className={cn(folha !== null && 'print:hidden')}>
        <CabecalhoDoModulo
          titulo="RH doméstico"
          subtitulo="QUADRO DE COLABORADORES E BANCO DE HORAS DA EQUIPE DA BOA VISTA"
        />

        <CabecalhoDeSecao
          className="border-b border-linha-forte pb-4"
          acoes={
            <div className="flex gap-2.5">
              <Button
                variant={aba === 'funcionarios' ? 'default' : 'outline'}
                aria-pressed={aba === 'funcionarios'}
                onClick={() => setAba('funcionarios')}
                className="text-sm/[normal]"
              >
                Funcionários
              </Button>
              <Button
                variant={aba === 'bancohoras' ? 'default' : 'outline'}
                aria-pressed={aba === 'bancohoras'}
                onClick={() => setAba('bancohoras')}
                className="shrink text-center text-sm/[normal] whitespace-normal"
              >
                Banco de Horas Equipe FBV
              </Button>
            </div>
          }
        />

        {aba === 'funcionarios' && (
          <>
            <SecaoCadastro
              titulo="Equipe ativa"
              descricao="FAZENDA BOA VISTA E ARMANDO PETRELLA"
              rotuloNovo="+ Novo funcionário"
              onNovo={podeEditar ? () => setPainel({ modo: 'novo-colaborador' }) : undefined}
              carregando={consultaPessoas.isPending}
              erro={consultaPessoas.error}
              onTentarDeNovo={() => void consultaPessoas.refetch()}
              vazia={colaboradoresAtivos.length === 0}
              mensagemVazia="Nenhum colaborador cadastrado ainda."
            >
              <>
                {equipeSelecionada.length > 0 && (
                  <BarraDeSelecao
                    quantidade={equipeSelecionada.length}
                    onExportarPdf={handleExportarPdf}
                    onExportarExcel={() => void handleExportarExcel()}
                    onLimpar={handleLimparSelecao}
                    gerandoPlanilha={gerandoPlanilha}
                  />
                )}
                {equipeSelecionada.length > 0 && planilhaFalhou && (
                  <p className="my-2 text-sm text-destructive print:hidden" role="alert">
                    A planilha não pôde ser gerada. Verifique a conexão com a internet e tente
                    novamente.
                  </p>
                )}
                <TabelaColaboradores
                  colaboradores={colaboradoresAtivos}
                  variante="ativos"
                  selecao={{
                    marcados: selecionados,
                    onAlternar: handleAlternarSelecao,
                    onAlternarTodos: handleAlternarTodos,
                  }}
                  onEditar={
                    podeEditar
                      ? (colaborador) => setPainel({ modo: 'edicao-colaborador', colaborador })
                      : undefined
                  }
                  onRemover={podeEditar ? handlePedirRemocao : undefined}
                />
              </>
            </SecaoCadastro>

            <SecaoCadastro
              titulo="Colaboradores desligados"
              carregando={consultaPessoas.isPending}
              erro={consultaPessoas.error}
              onTentarDeNovo={() => void consultaPessoas.refetch()}
              vazia={colaboradoresDesligados.length === 0}
              mensagemVazia="Nenhum colaborador desligado."
            >
              <TabelaColaboradores
                colaboradores={colaboradoresDesligados}
                variante="desligados"
                onEditar={
                  podeEditar
                    ? (colaborador) => setPainel({ modo: 'edicao-colaborador', colaborador })
                    : undefined
                }
                onRemover={podeEditar ? handlePedirRemocao : undefined}
              />
            </SecaoCadastro>
          </>
        )}

        {aba === 'bancohoras' && (
          <AbaBancoHoras
            tela={telaDoBanco}
            onTrocarTela={setTelaDoBanco}
            podeEditar={podeEditar}
            onImprimirResumo={(colaboradores) =>
              setFolha({
                tipo: 'banco-resumo',
                colaboradores,
                exportadaEm: new Date().toISOString(),
              })
            }
            onImprimirColaborador={(colaborador) =>
              setFolha({
                tipo: 'banco-colaborador',
                colaborador,
                exportadaEm: new Date().toISOString(),
              })
            }
          />
        )}

        <Dialog open={painel.modo !== 'fechado'} onOpenChange={handlePainelOpenChange}>
          <DialogContent>
            {painel.modo !== 'fechado' && (
              <>
                <DialogHeader>
                  <DialogTitle>
                    {painel.modo === 'novo-colaborador'
                      ? 'Novo funcionário'
                      : 'Editar funcionário'}
                  </DialogTitle>
                </DialogHeader>
                <FormularioColaborador
                  key={painel.modo === 'novo-colaborador' ? 'novo' : painel.colaborador.id}
                  colaborador={painel.modo === 'novo-colaborador' ? null : painel.colaborador}
                  onSalvo={() => setPainel({ modo: 'fechado' })}
                  onCancelar={() => setPainel({ modo: 'fechado' })}
                />
              </>
            )}
          </DialogContent>
        </Dialog>

        <AlertDialog open={aRemover !== null} onOpenChange={handleRemocaoOpenChange}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Remover {aRemover?.nomeCompleto ?? ''}?</AlertDialogTitle>
              <AlertDialogDescription>A pessoa sai do quadro.</AlertDialogDescription>
            </AlertDialogHeader>

            {erroDaRemocao !== null && (
              <p className="text-sm text-destructive" role="alert">
                {erroDaRemocao}
              </p>
            )}

            <AlertDialogFooter>
              <AlertDialogCancel disabled={excluirColaborador.isPending}>
                Cancelar
              </AlertDialogCancel>
              <AlertDialogAction
                disabled={excluirColaborador.isPending}
                onClick={(evento) => {
                  // O `AlertDialogAction` fecha a caixa ao ser clicado, e fechá-la
                  // antes da resposta da API desmontaria o estado de envio e
                  // esconderia a recusa por escala em uso.
                  evento.preventDefault();
                  void handleConfirmarRemocao();
                }}
              >
                {excluirColaborador.isPending ? 'Removendo…' : 'Remover'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      {folha?.tipo === 'equipe' && (
        <FolhaDaEquipe
          key={folha.exportadaEm}
          colaboradores={equipeSelecionada}
          exportadaEm={folha.exportadaEm}
          onConcluida={() => setFolha(null)}
        />
      )}
      {folha?.tipo === 'banco-resumo' && (
        <FolhaResumoBancoHoras
          key={folha.exportadaEm}
          colaboradores={folha.colaboradores}
          exportadaEm={folha.exportadaEm}
          onConcluida={() => setFolha(null)}
        />
      )}
      {folha?.tipo === 'banco-colaborador' && (
        <FolhaColaboradorBancoHoras
          key={folha.exportadaEm}
          colaborador={folha.colaborador}
          exportadaEm={folha.exportadaEm}
          onConcluida={() => setFolha(null)}
        />
      )}
    </>
  );
}
